import { MANGAL_HOUSES_EN } from "@ishvaram/domain-data/dosha-phrases";
/**
 * Jyotish glossary — canonical short definitions for technical Vedic terms
 * shown to cold SEO traffic on calculator/learn pages.
 *
 * Naming follows `.claude/rules/21-ishvaram-jyotish-vocabulary.md` (Google
 * Ads-verified Hinglish): standalone "dosha", compound "dosh"
 * (e.g. "kaal sarp dosh"), "kundali" / "kundli" both accepted, "lagna" /
 * "ascendant" interchangeable, etc.
 *
 * Definitions are 12–25 words, plain English, factual — not salesy. They
 * surface inside `<Tooltip>` popovers via the `<Term>` wrapper component.
 */

export interface GlossaryTerm {
  /** Short title shown bold above the body — usually the term itself, capitalised. */
  title: string;
  /** One-sentence definition (12–25 words, plain English). */
  text: string;
}

export const JYOTISH_GLOSSARY = {
  dosha: {
    title: "Dosha",
    text: "A planetary configuration in your birth chart that classical Vedic texts associate with specific life challenges, often paired with traditional remedies (upay).",
  },
  dosh: {
    title: "Dosh",
    text: "Same meaning as dosha — a challenging planetary pattern. The shorter form is used in compound names like Kaal Sarp Dosh, Mangal Dosh, Pitra Dosh.",
  },
  nakshatra: {
    title: "Nakshatra",
    text: "One of 27 lunar mansions dividing the zodiac into 13°20' segments. Your birth nakshatra is the one the Moon occupied at the moment of your birth.",
  },
  lagna: {
    title: "Lagna (Ascendant)",
    text: "The zodiac sign rising on the eastern horizon at your birth moment. It anchors the 12 houses of your kundali and changes roughly every two hours.",
  },
  ascendant: {
    title: "Ascendant (Lagna)",
    text: "The zodiac sign rising on the eastern horizon at your birth moment. It anchors the 12 houses of your kundali and changes roughly every two hours.",
  },
  dasha: {
    title: "Dasha",
    text: "A planetary period in Vedic astrology. The Vimshottari Dasha system divides life into nine major periods (Mahadashas) totalling 120 years, each ruled by one planet.",
  },
  mahadasha: {
    title: "Mahadasha",
    text: "A major planetary period in the Vimshottari Dasha system. Each of the nine planets rules a fixed span (e.g. Saturn 19 years, Jupiter 16 years).",
  },
  antardasha: {
    title: "Antardasha",
    text: "A sub-period inside a Mahadasha. Each Mahadasha contains nine Antardashas — one for each planet — that fine-tune the timing of life events.",
  },
  rashi: {
    title: "Rashi (Moon Sign)",
    text: "Your Vedic Moon sign — the zodiac sign the Moon occupied at birth. Vedic astrology treats the Moon sign as primary; the Sun sign is used in Western astrology.",
  },
  kundali: {
    title: "Kundali",
    text: "Your Vedic birth chart — a celestial map of where the nine planets sat across the 12 houses at the exact moment and place of your birth.",
  },
  kundli: {
    title: "Kundli",
    text: "Your Vedic birth chart — a celestial map of where the nine planets sat across the 12 houses at the exact moment and place of your birth.",
  },
  panchang: {
    title: "Panchang",
    text: "The Vedic almanac — five daily elements (tithi, vaar, nakshatra, yoga, karana) used to identify auspicious timings for rituals, travel, and decisions.",
  },
  choghadiya: {
    title: "Choghadiya",
    text: "An auspicious-timing system that splits each day and night into eight 90-minute slots labelled Shubh, Labh, Amrit (good) or Rog, Kaal, Udveg (avoid).",
  },
  "rahu kaal": {
    title: "Rahu Kaal",
    text: "A daily ~90-minute window ruled by Rahu, traditionally avoided for starting new ventures, travel, weddings, or major purchases.",
  },
  "kaal sarp dosh": {
    title: "Kaal Sarp Dosh",
    text: "A configuration where all seven visible planets sit on one side of the Rahu–Ketu axis. Tradition links it to delays and intense karmic struggles.",
  },
  "mangal dosh": {
    title: "Mangal Dosh",
    text: `Mars placed in the ${MANGAL_HOUSES_EN} house from the Lagna, with no benefic joining or aspecting it. Classically considered when matching marriage compatibility.`,
  },
  "pitra dosh": {
    title: "Pitra Dosh",
    text: "A planetary pattern (often involving Sun, Saturn, or Rahu) traditionally read as unresolved karma from ancestors, addressed via Shraddh and tarpan rituals.",
  },
  "sade sati": {
    title: "Sade Sati",
    text: "Saturn's 7.5-year transit through the 12th, 1st, and 2nd houses from your Moon sign — a karmic testing period everyone faces 2–3 times in life.",
  },
  navamsha: {
    title: "Navamsha (D9)",
    text: "The 9th divisional chart, derived by splitting each zodiac sign into 9 parts. Used to assess marriage prospects and the deeper strength of planets.",
  },
  d9: {
    title: "D9 (Navamsha)",
    text: "The 9th divisional chart, derived by splitting each zodiac sign into 9 parts. Used to assess marriage prospects and the deeper strength of planets.",
  },
  "kundali milan": {
    title: "Kundali Milan",
    text: "Vedic marriage compatibility check — matches two birth charts across eight koota factors (temperament, instinct, longevity, etc.) for a 36-point score.",
  },
  "gun milan": {
    title: "Gun Milan",
    text: "The 36-point Ashtakoot scoring inside Kundali Milan that compares two charts on eight compatibility factors. 18+ is the traditional pass threshold.",
  },
  upay: {
    title: "Upay",
    text: "A traditional remedy — mantra, fast, donation, gemstone, or temple ritual — prescribed to harmonise the energy of a challenging planet or dosha.",
  },
} satisfies Record<string, GlossaryTerm>;

export type GlossaryKey = keyof typeof JYOTISH_GLOSSARY;
